import React, { useCallback, useMemo } from 'react'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import { NoteType, NoteTypeWithStatus } from 'shared/components/Notes/models/note'
import MultiRoleSelect from 'shared/components/Notes/components/MultiRoleSelect'
import queryString from 'qs'
import flowRight from 'lodash/flowRight'
import TextField, { FieldLabel } from 'shared/components/TextField'
import styled from 'styled-components'
import Box from 'shared/components/Box'
import useQuery from 'shared/hooks/useQuery'
import { UserRoleTypeWithStatus } from 'pages/UserRole/model/role'

type Props = WrappedProps<NoteType> & {
  isEdit?: boolean
  isComment?: boolean
  allowRoleVisibility?: boolean
  allowedRoleLevels?: string[]
}

function CreateNoteDialog(props: Props) {
  const {
    formState,
    formHandlers,
    allowRoleVisibility = false,
    allowedRoleLevels = [],
    isEdit = false,
    isComment = false,
  } = props
  const { fields, errors } = formState
  const { onElementChange, onChange } = formHandlers

  const onFetchSuccess = useCallback(
    data => {
      if (allowedRoleLevels.length && data && data.length) {
        const roleIds = data.map((role: UserRoleTypeWithStatus) => role.id)
        onChange('role_ids', roleIds)
      }
    },
    [allowedRoleLevels, onChange],
  )

  const roleQueryString = useMemo(
    () =>
      queryString.stringify({
        communication: false,
        status: 'Active',
        level: allowedRoleLevels,
        fields: ['id', 'name', 'level'],
        sort: [{ column: 'level', direction: 'desc' }],
      }),
    [],
  )

  const [roleState] = useQuery<UserRoleTypeWithStatus[]>(
    { url: `/user/role?${roleQueryString}` },
    {
      initialData: [],
      isBase: false,
      skip: !allowRoleVisibility && !Boolean(allowedRoleLevels.length),
      onFetchSuccess,
    },
  )

  return (
    <CreateNoteDialogBodyContainer className="confirm-dialog-container">
      <Box>
        <FieldLabel>{isComment ? 'Comment' : 'Note'}</FieldLabel>
        <TextField
          id="message"
          placeholder="Write your message here"
          maxRows={3}
          rows={3}
          value={fields.message}
          onChange={onElementChange}
          error={Boolean(errors.message)}
          errorText={errors.message}
          disabled={false}
        />
      </Box>
      {allowRoleVisibility && !isEdit && (
        <Box mt="24px">
          <FieldLabel>Who can see this note?</FieldLabel>
          <MultiRoleSelect
            roles={roleState?.data || []}
            roleIds={fields?.role_ids || []}
            onChange={onChange}
            removeOrAddToUserRoleList={removeOrAddToUserRoleList}
          />
        </Box>
      )}
    </CreateNoteDialogBodyContainer>
  )

  function removeOrAddToUserRoleList(checked: boolean, method: string) {
    return checked ? fields?.role_ids?.concat([method]) : fields?.role_ids?.filter(e => e !== method)
  }
}

const WrappedCreateNoteDialog = flowRight(withDialog())(CreateNoteDialog)

const CreateNoteDialogBodyContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  padding: 40px 40px 0;
  width: 100%;
  max-width: 540px;
`

WrappedCreateNoteDialog.defaultProps = {
  continueIsDisabled(data?: NoteTypeWithStatus) {
    return data?.created_date && !data?.message
  },
}

export default WrappedCreateNoteDialog
